import type { TeamDDLOption } from '@/apis/lookup';
import type { TeamGroup } from '@/apis/teamGroups';
import { groupsForOptions } from '@/common/utils/teamGroups';

/**
 * `/monitor?team=Cell therapy,EU Ops` (or repeated `team=` params) selects
 * the teams one TV shows. Names are what people type into a TV's URL; ids
 * also work so links survive a team rename. A group's name or id stands
 * for its team teams.
 */
export const parseTeamParam = (params: URLSearchParams): string[] => {
  const values = params
    .getAll('team')
    .flatMap((value) => value.split(','))
    .map((value) => value.trim())
    .filter(Boolean);
  return Array.from(new Set(values));
};

export interface ResolvedTeams {
  /** Matched teams; a group contributes its team teams, never itself. */
  teams: TeamDDLOption[];
  /** For the header: each matched team's name, or a group's own name. */
  labels: string[];
  /** Requested values that match no team this user can see. */
  missing: string[];
  /** Closest real team names, for the "not found" message. */
  suggestions: string[];
}

/**
 * Case, spacing and punctuation do not count: a long account name typed on a
 * TV ("UNIVERSITY OF MICHIGAN NYC 0800033846") still finds
 * "University of Michigan - NYC (0800033846)". Letters and digits in any
 * script count, so "東京" or "Zürich" match too.
 */
const normalize = (value: string): string =>
  value
    .normalize('NFKC')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();

const words = (value: string): string[] =>
  normalize(value).split(' ').filter(Boolean);

/**
 * The teams one requested value stands for: a team or a group by id, then by
 * exact name (team first), then a unique partial team name. A group gives
 * its teams; `label` is what the header shows for the match.
 */
const findTeams = (
  value: string,
  available: TeamDDLOption[],
  groups: TeamGroup[]
): { teams: TeamDDLOption[]; label: string } | undefined => {
  const asTeam = (team: TeamDDLOption) => ({
    teams: [team],
    label: team.name || String(team.id),
  });
  const asGroup = (group: TeamGroup) => ({
    teams: group.team_ids
      .map((id) => available.find((team) => String(team.id) === id))
      .filter((team): team is TeamDDLOption => !!team),
    label: group.name,
  });

  const byId = available.find((team) => String(team.id) === value);
  if (byId) return asTeam(byId);
  const groupById = groups.find((group) => group.id === value);
  if (groupById) return asGroup(groupById);
  const wanted = normalize(value);
  if (!wanted) return undefined;
  const exact = available.find((team) => normalize(team.name || '') === wanted);
  if (exact) return asTeam(exact);
  const groupByName = groups.find((group) => normalize(group.name) === wanted);
  if (groupByName) return asGroup(groupByName);
  // A unique partial match ("michigan nyc") is unambiguous enough for a TV.
  const partial = available.filter((team) =>
    normalize(team.name || '').includes(wanted)
  );
  return partial.length === 1 ? asTeam(partial[0]) : undefined;
};

/** Up to `max` team names sharing the most words with what was typed. */
const suggestTeams = (
  missing: string[],
  names: string[],
  max = 5
): string[] => {
  const typed = new Set(missing.flatMap(words));
  return names
    .map((name) => ({
      name,
      shared: words(name).filter((word) => typed.has(word)).length,
    }))
    .filter((candidate) => candidate.shared > 0)
    .sort((a, b) => b.shared - a.shared || a.name.localeCompare(b.name))
    .slice(0, max)
    .map((candidate) => candidate.name);
};

/**
 * Matches by id, then by name (forgiving), against the user's teams. A group
 * (from `groups`) resolves to those of its teams the user can see; a group
 * with none is reported missing.
 */
export const resolveTeams = (
  requested: string[],
  available: TeamDDLOption[],
  groups: TeamGroup[] = []
): ResolvedTeams => {
  // Suggestions only offer groups with a team the user can see.
  const visibleGroups = groupsForOptions(
    groups,
    available.map((team) => String(team.id))
  );
  const teams: TeamDDLOption[] = [];
  const labels: string[] = [];
  const missing: string[] = [];
  requested.forEach((value) => {
    // Match against every group first: an empty group named "Ops" must not
    // fall through to a partial team match like "Ops Europe".
    const match = findTeams(value, available, groups);
    if (!match || match.teams.length === 0) {
      missing.push(value);
      return;
    }
    if (!labels.includes(match.label)) labels.push(match.label);
    match.teams.forEach((team) => {
      if (!teams.some((existing) => existing.id === team.id)) teams.push(team);
    });
  });
  return {
    teams,
    labels,
    missing,
    suggestions: missing.length
      ? suggestTeams(missing, [
          ...available.map((team) => team.name || ''),
          ...visibleGroups.map((group) => group.name),
        ])
      : [],
  };
};
